import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Plus, Pencil, X, Save, Trash2, User, Mail, Phone, MapPin, Percent,
  CalendarClock, Coins, Upload, Download, Receipt, FileText, ChevronDown,
} from "lucide-react";
import { api, apiErrorMessage, downloadCsvViaApi } from "../../api";
import { useToast } from "../../hooks/use-toast";
import ImportWizard from "../../components/app/ImportWizard";
import { Checkbox } from "../../components/ui/checkbox";
import { BulkActionsBar } from "../../components/app/BulkActionsBar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../../components/ui/dropdown-menu";

const CURRENCIES = [
  { code: "", label: "Account default" },
  { code: "AUD", label: "AUD — Australian Dollar" },
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "NZD", label: "NZD — New Zealand Dollar" },
  { code: "CAD", label: "CAD — Canadian Dollar" },
  { code: "SGD", label: "SGD — Singapore Dollar" },
  { code: "JPY", label: "JPY — Japanese Yen" },
];

const PALETTE = ["#22C55E", "#143A25", "#0E9F6E", "#4ADE80", "#F97316", "#0EA5E9", "#A855F7", "#F43F5E"];

export default function Clients() {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [drawerFor, setDrawerFor] = useState(null); // client obj or "new" or null
  const [addContactFor, setAddContactFor] = useState(null); // client id → open with focus on new contact
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try { setClients(await api.clients()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return clients;
    const needle = q.toLowerCase();
    return clients.filter((c) => {
      if (c.name.toLowerCase().includes(needle)) return true;
      return (c.contacts || []).some((k) =>
        [k.name, k.role, k.email, k.phone].some((v) => (v || "").toLowerCase().includes(needle))
      );
    });
  }, [clients, q]);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected client${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    const results = await Promise.allSettled(ids.map((id) => api.deleteClient(id)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) toast?.({ title: `${ok} client${ok === 1 ? "" : "s"} deleted` });
    if (failed > 0) toast?.({ title: `${failed} client(s) failed to delete`, description: "Please retry individually." });
    clearSelection();
    await load();
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f]">Clients</div>
          <h1 className="font-display text-4xl mt-1">Clients</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setDrawerFor("new")}
                  data-testid="new-client-btn"
                  className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
            <Plus size={14} /> New client
          </button>
          <BulkActionsBar
            count={selected.size}
            onClear={clearSelection}
            testidPrefix="clients"
            actions={[
              { key: "delete", label: `Delete selected${selected.size ? ` (${selected.size})` : ""}`, icon: Trash2, danger: true, onClick: bulkDelete },
            ]}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="clients-import-export-btn"
                      className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
                <Upload size={14} /> Import/Export <ChevronDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white min-w-[180px]">
              <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="clients-menu-import">
                <Upload size={14} /> Import clients
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => downloadCsvViaApi("/clients/export.csv", {}, "clients.csv")}
                data-testid="clients-menu-export"
              >
                <Download size={14} /> Export clients
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mt-6 relative max-w-lg">
        <Search size={14} className="absolute left-3 top-2.5 text-[#6a736f]" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Filter by client or contact"
               data-testid="clients-search"
               className="w-full pl-9 pr-3 py-2 rounded-md border border-cream text-sm bg-white" />
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="text-sm text-[#6a736f]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-cream rounded-xl p-8 text-center text-sm text-[#6a736f]"
               data-testid="clients-empty">
            {clients.length === 0
              ? <>No clients yet. <button onClick={() => setDrawerFor("new")} className="text-coral font-medium">Add your first client</button>.</>
              : "No matches for that search."}
          </div>
        ) : filtered.map((c) => (
          <ClientCard
            key={c.id} client={c}
            selected={selected.has(c.id)}
            onToggleSelect={() => toggleSelect(c.id)}
            onEdit={() => setDrawerFor(c)}
            onAddContact={() => { setDrawerFor(c); setAddContactFor(c.id); }}
            onEditContact={(contactId) => { setDrawerFor(c); setAddContactFor(null); /* drawer scrolls to contacts */ }}
          />
        ))}
      </div>

      {drawerFor && (
        <ClientDrawer
          initial={drawerFor === "new" ? null : drawerFor}
          startInContactsFocus={!!addContactFor}
          onClose={() => { setDrawerFor(null); setAddContactFor(null); }}
          onSaved={async () => { setDrawerFor(null); setAddContactFor(null); await load(); }}
          onDeleted={async () => { setDrawerFor(null); await load(); toast?.({ title: "Client deleted" }); }}
          toast={toast}
        />
      )}

      <ImportWizard
        entity="clients"
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => load()}
        toast={toast}
      />
    </div>
  );
}

function ClientCard({ client, selected, onToggleSelect, onEdit, onAddContact, onEditContact }) {
  const contacts = client.contacts || [];
  return (
    <div className="bg-white border border-cream rounded-xl overflow-hidden" data-testid={`client-card-${client.id}`}>
      <div className="flex items-center justify-between px-4 py-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Checkbox checked={selected} onCheckedChange={onToggleSelect}
                    data-testid={`select-client-${client.id}`} />
          <button onClick={onEdit} data-testid={`edit-client-${client.id}`}
                  className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
            <Pencil size={12} /> Edit
          </button>
          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: client.color || "#22C55E" }} />
          <button onClick={onEdit} className="font-semibold truncate text-forest hover:text-coral text-left">
            {client.name}
          </button>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to={`/app/invoices/new?client_id=${client.id}`}
                data-testid={`new-invoice-${client.id}`}
                className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
            <Receipt size={12} /> New invoice
          </Link>
          <Link to={`/app/invoices?client_id=${client.id}`}
                data-testid={`view-invoices-${client.id}`}
                className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
            <FileText size={12} /> Invoices
          </Link>
          <button onClick={onAddContact}
                  data-testid={`add-contact-${client.id}`}
                  className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
            <Plus size={12} /> Add contact
          </button>
        </div>
      </div>

      {contacts.length > 0 && (
        <div className="border-t border-cream divide-y divide-[#EAF3EE]">
          {contacts.map((k) => (
            <div key={k.id} className="flex items-center gap-3 px-4 py-3" data-testid={`contact-row-${k.id}`}>
              <button onClick={() => onEditContact(k.id)}
                      data-testid={`edit-contact-${k.id}`}
                      className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium shrink-0">
                <Pencil size={12} /> Edit
              </button>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{k.name}</div>
                <div className="text-xs text-[#6a736f] truncate">
                  {[k.role, k.email, k.phone && `${k.phone} (mobile)`].filter(Boolean).join(", ")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientDrawer({ initial, onClose, onSaved, onDeleted, toast, startInContactsFocus }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    color: initial?.color || PALETTE[0],
    address: initial?.address || "",
    preferred_currency: initial?.preferred_currency || "",
    invoice_due_days: initial?.invoice_due_days ?? "",
    tax_percent: initial?.tax_percent ?? "",
    tax_percent_2: initial?.tax_percent_2 ?? "",
    discount_percent: initial?.discount_percent ?? "",
  }));
  const [contacts, setContacts] = useState(() => initial?.contacts?.map((c) => ({ ...c })) || []);
  const [showSecondTax, setShowSecondTax] = useState(!!initial?.tax_percent_2);
  const [dueMode, setDueMode] = useState(() => {
    if (initial?.invoice_due_days == null) return "default";
    return "custom";
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const patchContact = (idx, upd) =>
    setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, ...upd } : c)));
  const removeContact = (idx) => setContacts((prev) => prev.filter((_, i) => i !== idx));

  const addContact = () => setContacts((prev) => [...prev, { id: null, name: "", role: "", email: "", phone: "" }]);

  useEffect(() => {
    if (startInContactsFocus && contacts.length === 0) addContact();
    // eslint-disable-next-line
  }, [startInContactsFocus]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        address: form.address.trim() || null,
        preferred_currency: form.preferred_currency || null,
        invoice_due_days: dueMode === "custom" && form.invoice_due_days !== "" ? Number(form.invoice_due_days) : null,
        tax_percent: form.tax_percent === "" ? null : Number(form.tax_percent),
        tax_percent_2: showSecondTax && form.tax_percent_2 !== "" ? Number(form.tax_percent_2) : null,
        discount_percent: form.discount_percent === "" ? null : Number(form.discount_percent),
        contacts: contacts
          .filter((c) => (c.name || "").trim())
          .map((c) => ({
            id: c.id || null,
            name: c.name.trim(),
            role: (c.role || "").trim() || null,
            email: (c.email || "").trim() || null,
            phone: (c.phone || "").trim() || null,
          })),
      };
      if (isEdit) {
        await api.updateClient(initial.id, payload);
        toast?.({ title: "Client updated" });
      } else {
        await api.createClient(payload);
        toast?.({ title: "Client created" });
      }
      await onSaved?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save client."));
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete "${initial.name}"? Projects that reference this client will keep their link.`)) return;
    try {
      await api.deleteClient(initial.id);
      await onDeleted?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't delete client."));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="client-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form onSubmit={submit}
            className="w-full max-w-lg bg-white border-l border-cream shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-cream flex items-center justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Edit client" : "New client"}</h2>
          <button type="button" onClick={onClose} className="text-[#6a736f]" data-testid="close-client-drawer">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5 flex-1">
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Client name</span>
            <input required value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="cd-name"
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>

          <div>
            <div className="text-xs font-semibold text-[#3f4a44] mb-1.5">Colour</div>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((c) => (
                <button type="button" key={c} onClick={() => setForm({ ...form, color: c })}
                        className={`h-7 w-7 rounded-full border-2 ${form.color === c ? "border-forest" : "border-transparent"}`}
                        style={{ background: c }} />
              ))}
            </div>
          </div>

          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44] flex items-center gap-1"><MapPin size={12} /> Address</span>
            <textarea rows={3} value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Street&#10;Suburb State Postcode"
                      data-testid="cd-address"
                      className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>

          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44] flex items-center gap-1"><Coins size={12} /> Preferred currency</span>
            <select value={form.preferred_currency}
                    onChange={(e) => setForm({ ...form, preferred_currency: e.target.value })}
                    data-testid="cd-currency"
                    className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>

          {/* Account details divider */}
          <div className="border-t border-cream pt-5">
            <div className="text-xs font-semibold text-[#3f4a44] mb-3">Account details</div>

            <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
              <div className="text-sm inline-flex items-center gap-1"><CalendarClock size={12} /> Invoice due date</div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={dueMode} onChange={(e) => setDueMode(e.target.value)}
                        data-testid="cd-due-mode"
                        className="border border-cream rounded-md px-2 py-1.5 text-sm bg-white">
                  <option value="default">Account default</option>
                  <option value="custom">Custom</option>
                </select>
                {dueMode === "custom" && (
                  <>
                    <input type="number" min={0} value={form.invoice_due_days}
                           onChange={(e) => setForm({ ...form, invoice_due_days: e.target.value })}
                           data-testid="cd-due-days"
                           className="w-16 border border-cream rounded-md px-2 py-1.5 text-sm" />
                    <span className="text-xs text-[#6a736f]">days after issue date</span>
                  </>
                )}
              </div>

              <div className="text-sm inline-flex items-center gap-1 mt-3"><Percent size={12} /> Tax</div>
              <div className="flex items-center gap-1 mt-3">
                <input type="number" min={0} step="0.01" value={form.tax_percent}
                       onChange={(e) => setForm({ ...form, tax_percent: e.target.value })}
                       data-testid="cd-tax"
                       className="w-20 border border-cream rounded-md px-2 py-1.5 text-sm" />
                <span className="text-xs text-[#6a736f]">%</span>
                {!showSecondTax ? (
                  <button type="button" onClick={() => setShowSecondTax(true)}
                          className="ml-2 text-xs text-coral hover:underline">Enable second tax</button>
                ) : (
                  <>
                    <span className="ml-2 text-xs text-[#6a736f]">+</span>
                    <input type="number" min={0} step="0.01" value={form.tax_percent_2}
                           onChange={(e) => setForm({ ...form, tax_percent_2: e.target.value })}
                           data-testid="cd-tax-2"
                           className="w-20 border border-cream rounded-md px-2 py-1.5 text-sm" />
                    <span className="text-xs text-[#6a736f]">%</span>
                  </>
                )}
              </div>

              <div className="text-sm inline-flex items-center gap-1 mt-3"><Percent size={12} /> Discount</div>
              <div className="flex items-center gap-1 mt-3">
                <input type="number" min={0} step="0.01" value={form.discount_percent}
                       onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                       data-testid="cd-discount"
                       className="w-20 border border-cream rounded-md px-2 py-1.5 text-sm" />
                <span className="text-xs text-[#6a736f]">%</span>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="border-t border-cream pt-5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-[#3f4a44]">Contacts</div>
              <button type="button" onClick={addContact}
                      data-testid="cd-add-contact"
                      className="btn-outline-forest inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium">
                <Plus size={12} /> Add contact
              </button>
            </div>
            {contacts.length === 0 ? (
              <div className="text-xs text-[#6a736f] mt-3">No contacts yet.</div>
            ) : (
              <div className="mt-3 space-y-3">
                {contacts.map((c, idx) => (
                  <div key={idx} className="border border-cream rounded-lg p-3 space-y-2"
                       data-testid={`contact-editor-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-forest inline-flex items-center gap-1">
                        <User size={12} /> {c.name || "New contact"}
                      </div>
                      <button type="button" onClick={() => removeContact(idx)}
                              data-testid={`remove-contact-${idx}`}
                              className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input value={c.name} onChange={(e) => patchContact(idx, { name: e.target.value })}
                             placeholder="Name" required
                             data-testid={`contact-name-${idx}`}
                             className="border border-cream rounded-md px-2 py-1.5 text-sm" />
                      <input value={c.role || ""} onChange={(e) => patchContact(idx, { role: e.target.value })}
                             placeholder="Role / title"
                             data-testid={`contact-role-${idx}`}
                             className="border border-cream rounded-md px-2 py-1.5 text-sm" />
                      <div className="relative">
                        <Mail size={12} className="absolute left-2 top-2.5 text-[#6a736f]" />
                        <input value={c.email || ""} onChange={(e) => patchContact(idx, { email: e.target.value })}
                               type="email" placeholder="email@example.com"
                               data-testid={`contact-email-${idx}`}
                               className="pl-6 w-full border border-cream rounded-md px-2 py-1.5 text-sm" />
                      </div>
                      <div className="relative">
                        <Phone size={12} className="absolute left-2 top-2.5 text-[#6a736f]" />
                        <input value={c.phone || ""} onChange={(e) => patchContact(idx, { phone: e.target.value })}
                               placeholder="Phone"
                               data-testid={`contact-phone-${idx}`}
                               className="pl-6 w-full border border-cream rounded-md px-2 py-1.5 text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && <div className="text-sm text-red-600" data-testid="cd-error">{err}</div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-cream px-6 py-4 flex items-center justify-between gap-2">
          <div>
            {isEdit && (
              <button type="button" onClick={del}
                      data-testid="cd-delete"
                      className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                <Trash2 size={14} /> Delete client
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
                    className="btn-outline-forest px-4 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={busy}
                    data-testid="cd-save"
                    className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
              <Save size={14} /> {busy ? "Saving…" : "Save client"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
